import { useEffect, useState } from 'react';
import {
  useDeleteMyProsReview,
  useProsReviews,
  useReportProsReview,
  useSaveProsReview,
} from '@tark/shared/hooks';
import type {
  ProviderRatingSummary,
  ProviderReviewItem,
  ReportProviderReviewPayload,
} from '@tark/shared/types';
import { CheckCircle2, Flag, Loader2, Star } from 'lucide-react';

export function ProviderRatingLine({ summary }: { summary?: ProviderRatingSummary | null }) {
  const count = summary?.reviewCount ?? 0;
  if (!count || summary?.averageRating == null) {
    return <span className="text-sm font-medium text-text-muted">New on TARK</span>;
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-sm">
      <Star className="h-4 w-4 fill-warning text-warning" />
      <strong className="font-semibold text-text">{summary.averageRating.toFixed(1)}</strong>
      <span className="text-text-muted">{count} {count === 1 ? 'review' : 'reviews'}</span>
    </span>
  );
}

function formattedDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function ProviderReviewsPanel({
  providerId,
  initialSummary,
}: {
  providerId: number;
  initialSummary?: ProviderRatingSummary | null;
}) {
  const reviews = useProsReviews(providerId);
  const saveReview = useSaveProsReview(providerId);
  const deleteReview = useDeleteMyProsReview(providerId);
  const reportReview = useReportProsReview(providerId);
  const [editing, setEditing] = useState(false);
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [reportingId, setReportingId] = useState<number | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const data = reviews.data;
  const ownReview = data?.myReview ?? null;
  const canWrite = Boolean(data?.eligibility.eligible || ownReview);
  const visibleItems = showAll ? data?.items ?? [] : (data?.items ?? []).slice(0, 3);

  useEffect(() => {
    if (!editing) return;
    setRating(ownReview?.rating ?? 0);
    setReviewText(ownReview?.reviewText ?? '');
  }, [editing, ownReview]);

  const submit = async () => {
    if (!rating) return;
    setNotice(null);
    try {
      await saveReview.mutateAsync({
        payload: { rating, reviewText: reviewText.trim() || undefined },
        update: Boolean(ownReview),
      });
      setEditing(false);
      setNotice('Review saved.');
    } catch {
      setNotice('Review was not saved. Please try again.');
    }
  };

  const remove = async () => {
    if (!window.confirm('Remove your review?')) return;
    setNotice(null);
    try {
      await deleteReview.mutateAsync();
      setEditing(false);
      setNotice('Your review was removed.');
    } catch {
      setNotice('Review was not removed. Please try again.');
    }
  };

  const report = async (reviewId: number, reasonCode: ReportProviderReviewPayload['reasonCode']) => {
    setNotice(null);
    try {
      await reportReview.mutateAsync({ reviewId, payload: { reasonCode } });
      setReportingId(null);
      setNotice('Report received. Thank you.');
    } catch {
      setNotice('Report was not sent. Please try again.');
    }
  };

  return (
    <section className="rounded-lg border border-border bg-white p-5 shadow-sm md:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-text-subtle">Trust</p>
          <h2 className="mt-1 text-xl font-semibold text-text">Reviews</h2>
          <div className="mt-1"><ProviderRatingLine summary={data?.summary ?? initialSummary} /></div>
        </div>
        {canWrite ? (
          <button
            type="button"
            onClick={() => setEditing((value) => !value)}
            className="min-h-11 rounded-md px-3 text-sm font-semibold text-primary hover:bg-primary/5"
          >
            {editing ? 'Cancel' : ownReview ? 'Edit review' : 'Write a review'}
          </button>
        ) : null}
      </div>

      {ownReview?.status === 'PENDING' ? (
        <p className="mt-4 rounded-md bg-warning/10 px-3 py-2 text-sm text-text-muted">Your review is being checked.</p>
      ) : null}

      {editing ? (
        <div className="mt-5 border-t border-border pt-5">
          <div className="flex gap-1" aria-label="Rating">
            {Array.from({ length: 5 }, (_, index) => (
              <button
                key={index}
                type="button"
                onClick={() => setRating(index + 1)}
                aria-label={`${index + 1} star${index ? 's' : ''}`}
                className="flex h-11 w-11 items-center justify-center rounded-md hover:bg-warning/10"
              >
                <Star className={`h-6 w-6 ${index < rating ? 'fill-warning text-warning' : 'text-text-subtle'}`} />
              </button>
            ))}
          </div>
          <textarea
            value={reviewText}
            onChange={(event) => setReviewText(event.target.value)}
            maxLength={800}
            rows={4}
            placeholder="Share helpful details (optional)"
            className="mt-3 w-full resize-y rounded-md border border-border bg-surface px-3 py-2 text-sm text-text outline-none focus:border-primary focus:ring-2 focus:ring-primary/15"
          />
          <div className="mt-1 flex items-center justify-between gap-3">
            <span className="text-xs text-text-subtle">{reviewText.length}/800</span>
            <button
              type="button"
              disabled={!rating || saveReview.isPending}
              onClick={() => void submit()}
              className="min-h-11 rounded-md bg-primary px-5 text-sm font-semibold text-white disabled:opacity-50"
            >
              {saveReview.isPending ? 'Saving...' : 'Save review'}
            </button>
          </div>
        </div>
      ) : null}

      {reviews.isLoading ? (
        <div className="flex min-h-24 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
      ) : reviews.isError ? (
        <div className="mt-5">
          <p className="text-sm text-text-muted">Reviews are unavailable.</p>
          <button type="button" onClick={() => void reviews.refetch()} className="mt-2 text-sm font-semibold text-primary">Retry</button>
        </div>
      ) : visibleItems.length ? (
        <div className="mt-5 divide-y divide-border">
          {visibleItems.map((review) => (
            <ReviewRow
              key={review.reviewId}
              review={review}
              reporting={reportingId === review.reviewId}
              onToggleReport={() => setReportingId((current) => current === review.reviewId ? null : review.reviewId)}
              onReport={(reason) => void report(review.reviewId, reason)}
            />
          ))}
          {(data?.items.length ?? 0) > 3 ? (
            <button type="button" onClick={() => setShowAll((value) => !value)} className="min-h-11 text-sm font-semibold text-primary">
              {showAll ? 'Show less' : 'Show all'}
            </button>
          ) : null}
        </div>
      ) : (
        <p className="mt-5 text-sm text-text-muted">No reviews yet.</p>
      )}

      {ownReview ? (
        <button type="button" onClick={() => void remove()} className="mt-3 min-h-10 text-sm font-semibold text-danger">
          Remove my review
        </button>
      ) : null}
      {notice ? <p className="mt-3 text-sm text-text-muted" role="status">{notice}</p> : null}
    </section>
  );
}

function ReviewRow({
  review,
  reporting,
  onToggleReport,
  onReport,
}: {
  review: ProviderReviewItem;
  reporting: boolean;
  onToggleReport: () => void;
  onReport: (reason: ReportProviderReviewPayload['reasonCode']) => void;
}) {
  return (
    <article className="py-4 first:pt-0 last:pb-0">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-semibold text-text">{review.reviewerDisplayName}</p>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <span className="inline-flex gap-0.5">
              {Array.from({ length: 5 }, (_, index) => (
                <Star key={index} className={`h-3.5 w-3.5 ${index < review.rating ? 'fill-warning text-warning' : 'text-text-subtle'}`} />
              ))}
            </span>
            <span className="text-xs text-text-subtle">{formattedDate(review.updatedAt)}</span>
          </div>
        </div>
        {!review.ownReview ? (
          <button type="button" onClick={onToggleReport} className="flex h-10 w-10 items-center justify-center rounded-md text-text-muted hover:bg-surface" aria-label="Report review">
            <Flag className="h-4 w-4" />
          </button>
        ) : null}
      </div>
      {review.reviewText ? <p className="mt-3 max-w-3xl text-sm leading-6 text-text-muted">{review.reviewText}</p> : null}
      {review.verifiedInteraction ? (
        <p className="mt-2 flex items-center gap-1.5 text-xs font-medium text-text-subtle">
          <CheckCircle2 className="h-3.5 w-3.5 text-success" /> Verified TARK interaction
        </p>
      ) : null}
      {reporting ? (
        <div className="mt-3 flex flex-wrap gap-2 rounded-md bg-surface p-2">
          {([
            ['SPAM', 'Spam'],
            ['HARASSMENT', 'Harassment'],
            ['FALSE_INFORMATION', 'False information'],
            ['PRIVACY', 'Privacy'],
          ] as const).map(([value, label]) => (
            <button key={value} type="button" onClick={() => onReport(value)} className="min-h-9 rounded-md border border-border bg-white px-3 text-xs font-semibold text-text-muted hover:border-primary">
              {label}
            </button>
          ))}
        </div>
      ) : null}
    </article>
  );
}